import { type ButtonHTMLAttributes } from "react";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost" | "link";
export type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
}

const BASE =
  "inline-flex items-center justify-center gap-2 rounded-control text-center text-base font-semibold transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50";

/*
 * The primary border is the brand's foreground: invisible against the fill on
 * light, and the 1px edge that keeps the raw brand fill readable on the dark
 * surface. The danger fill inks itself in the surface, which clears the red
 * tone in both themes.
 */
const VARIANT: Record<ButtonVariant, string> = {
  primary: "border border-brand-fg bg-brand text-brand-ink hover:bg-brand-hover",
  secondary: "border border-line-strong bg-surface text-ink hover:bg-canvas",
  danger: "border border-tone-red bg-tone-red text-surface hover:bg-tone-red/90",
  ghost: "border border-transparent bg-transparent text-ink hover:bg-canvas",
  link: "border border-transparent bg-transparent text-brand-fg underline-offset-2 hover:underline",
};

/** Every size is a 44px touch target below lg; only the small one tightens, from lg. */
const HEIGHT: Record<ButtonSize, string> = {
  sm: "min-h-11 lg:min-h-9",
  md: "min-h-11",
  lg: "min-h-12",
};

const PADDING: Record<ButtonSize, string> = {
  sm: "px-3",
  md: "px-4",
  lg: "px-6",
};

/**
 * The classes `<Button variant={variant} size={size}>` renders, for a link or
 * any other element drawn as a button. A link-variant button keeps the touch
 * height and drops the padding, so its text lines up with the copy around it.
 */
export function buttonClasses(variant: ButtonVariant = "primary", size: ButtonSize = "md"): string {
  const padding = variant === "link" ? "" : ` ${PADDING[size]}`;
  return `${BASE} ${VARIANT[variant]} ${HEIGHT[size]}${padding}`;
}

function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  disabled,
  children,
  className = "",
  ...props
}: ButtonProps) {
  const classes = buttonClasses(variant, size);
  return (
    <button
      type="button"
      disabled={disabled || loading}
      className={className ? `${classes} ${className}` : classes}
      {...props}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
}
